import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import TileCard from '../components/TileCard';
import InstallPrompt, { useInstallState } from '../components/InstallPrompt';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Newspaper, Facebook, Twitter, Linkedin, Instagram, Youtube, LogIn, Eye, EyeOff, X, Inbox, Bell, BellRing, User, ChevronRight, Trash2, Check, CheckCheck, RefreshCw, Link2, Calendar, MapPin, Clock, Users } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { subscribeToPushNotifications, isPushNotificationSupported, getPushNotificationStatus } from '../utils/pushNotifications';
import { countEnabledSocialLinks, socialLinkSizeClasses, SOCIAL_URL_KEYS } from '../lib/socialLinks';

// Custom TikTok icon component
const TikTokIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Custom Bluesky butterfly icon
const BlueSkyIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 568 501" fill="currentColor">
    <path d="M123.121 33.664C188.241 82.553 258.281 181.68 284 234.873c25.719-53.192 95.759-152.32 160.879-201.209C491.866-1.611 568-28.906 568 57.947c0 17.346-9.945 145.713-15.778 166.555-20.275 72.453-94.155 90.933-159.875 79.748C507.222 323.8 536.444 388.56 473.333 453.32c-119.86 122.992-172.272-30.859-185.702-70.281-2.462-7.227-3.614-10.608-3.631-7.74-.017-2.868-1.169.513-3.631 7.74-13.43 39.422-65.842 193.273-185.702 70.281-63.111-64.76-33.889-129.52 80.986-149.065-65.72 11.185-139.6-7.295-159.875-79.748C9.945 203.659 0 75.291 0 57.947 0-28.906 76.134-1.611 123.121 33.664Z"/>
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Auto-contrast text colour for a given hex background
const getButtonTextColor = (hex) => {
  if (!hex) return '#FFFFFF';
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1E293B' : '#FFFFFF';
};

// ---- Inline RSVP Modal for PublicPortal ----
function PortalRsvpModal({ event, customerId, currentUser, primaryColor, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [form, setForm] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    party_size: 1
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/public/customers/${customerId}/nexus-events/${event.id}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(event))
      .finally(() => setLoadingDetail(false));
  }, [event.id, customerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { ...form };
      if (!body.party_size) body.party_size = 1;
      const res = await axios.post(
        `${API_URL}/public/customers/${customerId}/nexus-events/${event.id}/rsvp`,
        body
      );
      setResult(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'RSVP failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const fields = detail?.rsvp_fields || [
    { key: 'name', label: 'Full name', type: 'text', required: true },
    { key: 'email', label: 'Email address', type: 'email', required: true },
  ];

  const statusColors = {
    confirmed: 'text-green-700 bg-green-50 border-green-200',
    waitlisted: 'text-amber-700 bg-amber-50 border-amber-200',
    full: 'text-red-700 bg-red-50 border-red-200',
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="rsvp-modal">
        <DialogHeader>
          <DialogTitle className="text-base">{event.title}</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className={`rounded-lg border p-4 text-sm ${statusColors[result.status] || ''}`}>
            <p className="font-semibold capitalize mb-1">
              {result.status === 'confirmed' ? "You're registered!" : result.status}
            </p>
            <p>{result.message}</p>
            {result.event?.location && (
              <p className="mt-2 flex items-center gap-1 text-xs">
                <MapPin className="w-3 h-3" />{result.event.location}
              </p>
            )}
            <Button className="w-full mt-3" variant="outline" onClick={onClose} size="sm">
              Close
            </Button>
          </div>
        ) : loadingDetail ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground space-y-1 mb-3">
              {!event.all_day && (
                <p className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(event.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {event.end ? ` – ${new Date(event.end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
                </p>
              )}
              {event.location && (
                <p className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{event.location}
                </p>
              )}
              {event.spots_left !== null && event.spots_left !== undefined && (
                <p className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {event.spots_left === 0 ? 'Event full' : `${event.spots_left} spots left`}
                </p>
              )}
            </div>
            {detail?.description_html && (
              <div
                className="text-xs text-gray-600 mb-3 max-h-24 overflow-y-auto border-b pb-3"
                dangerouslySetInnerHTML={{ __html: detail.description_html }}
              />
            )}
            {event.rsvp_enabled ? (
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="rsvp-form">
                {fields.map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label}{f.required ? ' *' : ''}</Label>
                    <Input
                      type={f.type || 'text'}
                      required={f.required}
                      value={form[f.key] ?? (f.default ?? '')}
                      min={f.min}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value
                      }))}
                      data-testid={`rsvp-field-${f.key}`}
                    />
                  </div>
                ))}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || (event.rsvp_full && !event.waitlist_enabled)}
                  style={{ backgroundColor: event.rsvp_full && !event.waitlist_enabled ? undefined : primaryColor }}
                  data-testid="rsvp-submit-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {event.rsvp_full && !event.waitlist_enabled
                    ? 'Event Full'
                    : event.rsvp_full && event.waitlist_enabled
                      ? 'Join Waitlist'
                      : 'Confirm RSVP'}
                </Button>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">
                No RSVP required for this event.
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PublicPortal() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tiles, setTiles] = useState([]);
  const [topArticles, setTopArticles] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gridCols, setGridCols] = useState(3);
  const [requireAuth, setRequireAuth] = useState(false);
  const [publicTilesEnabled, setPublicTilesEnabled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '', rememberDays: '7' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [forgotPwEmail, setForgotPwEmail] = useState('');
  const [forgotPwLoading, setForgotPwLoading] = useState(false);
  const [forgotPwSent, setForgotPwSent] = useState(false);
  
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [nexusArticles, setNexusArticles] = useState([]);
  const [nexusEvents, setNexusEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [activeChatTile, setActiveChatTile] = useState(null);
  
  // Portal-specific token key to avoid interfering with main app auth
  const portalTokenKey = `portal_token_${customerId}`;
  
  // Helper functions for portal authentication
  const getPortalToken = () => localStorage.getItem(portalTokenKey);
  const setPortalToken = (token) => localStorage.setItem(portalTokenKey, token);
  const clearPortalToken = () => localStorage.removeItem(portalTokenKey);
  
  // Inbox panel state
  const [showInboxPanel, setShowInboxPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [inboxTab, setInboxTab] = useState('notifications'); // 'notifications' or 'profile'
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [deletingNotification, setDeletingNotification] = useState(null);
  
  // Push notification state
  const [pushStatus, setPushStatus] = useState('checking'); // 'checking', 'unsupported', 'denied', 'default', 'granted', 'subscribed'
  const [subscribingPush, setSubscribingPush] = useState(false);
  const [showIOSInstallGuide, setShowIOSInstallGuide] = useState(false);
  
  // Detect iOS and standalone mode - use state to ensure proper client-side detection
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  // CRITICAL: For iOS PWA support, we need to ensure meta tags are set correctly
  // iOS reads meta tags when user taps "Add to Home Screen"
  // The React app dynamically updates these in the useEffect below
  // 
  // IMPORTANT iOS BEHAVIOR: iOS Safari ignores manifest start_url and instead uses
  // the CURRENT URL in the address bar when "Add to Home Screen" is tapped.
  // This means users MUST be on the portal page (not root) when adding to home screen.
  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    
    setIsIOS(ios);
    setIsStandalone(standalone);
    
    // CRITICAL FIX: Explicitly set the URL in browser history to ensure iOS Safari captures the correct path
    // iOS Safari may read from history stack rather than address bar when adding to Home Screen
    const expectedPath = `/portal/${customerId}`;
    if (window.location.pathname !== expectedPath) {
      console.log('iOS PWA Fix: Correcting URL path via history.replaceState');
      window.history.replaceState({}, '', expectedPath);
    }
    
    // Also try pushState to add it to the history stack
    // This ensures the URL is definitely in the browser's history
    window.history.pushState({ portal: customerId }, '', expectedPath);
    
    // Debug logging for iOS PWA troubleshooting
    console.log('=== iOS PWA Debug Info ===');
    console.log('Platform: ', ios ? 'iOS' : 'Other');
    console.log('Standalone mode:', standalone);
    console.log('Current URL:', window.location.href);
    console.log('Current pathname:', window.location.pathname);
    console.log('Expected portal path:', expectedPath);
    console.log('URL matches expected:', window.location.pathname === expectedPath);
    console.log('history.pushState applied:', true);
    console.log('========================');
    
    // For iOS Safari (not standalone), show a message about adding to home screen
    if (ios && !standalone) {
      console.log('iOS Safari detected - URL has been set via history.pushState');
    }
  }, [customerId]);

  // Reset page meta to neutral values as soon as customerId changes (before new data loads)
  // This prevents a previous tenant's title/icon from persisting during the loading spinner
  useEffect(() => {
    document.title = 'Loading...';
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.content = '#2563EB';
    // Remove any tenant-specific apple-touch-icon so no stale logo shows
    document.querySelectorAll('link[rel="apple-touch-icon"][data-tenant]').forEach(el => el.remove());
  }, [customerId]);

  // Inject per-tenant PWA manifest + Apple meta tags once customer data is loaded
  useEffect(() => {
    if (!customer) return;

    const manifestUrl = `${API_URL}/public/customers/${customerId}/manifest.json`;

    // Update <link rel="manifest">
    let manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = manifestUrl;

    // Update theme-color
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = customer.primary_color || '#2563EB';

    // Apple-specific: app title shown under the icon on iOS home screen
    let appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitleMeta) {
      appleTitleMeta = document.createElement('meta');
      appleTitleMeta.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitleMeta);
    }
    appleTitleMeta.content = customer.name || 'Taptile';

    // Apple touch icon (shown on iOS home screen before manifest icon loads)
    if (customer.pwa_icon_url) {
      let appleIcon = document.querySelector('link[rel="apple-touch-icon"][data-tenant]');
      if (!appleIcon) {
        appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.setAttribute('data-tenant', 'true');
        document.head.appendChild(appleIcon);
      }
      appleIcon.href = customer.pwa_icon_url;
    }

    // Page title
    document.title = customer.name || 'Taptile';

    return () => {
      // Restore defaults on unmount
      document.title = 'Taptile';
      if (manifestLink) manifestLink.href = '/manifest.json';
    };
  }, [customer, customerId]);

  // Check push notification status when authenticated
  useEffect(() => {
    const checkPushStatus = async () => {
      if (isAuthenticated && isPushNotificationSupported()) {
        const status = await getPushNotificationStatus();
        setPushStatus(status);
        
        // Auto-subscribe if permission is granted but not yet subscribed
        if (status === 'granted') {
          handleEnablePushNotifications();
        }
      } else if (!isPushNotificationSupported()) {
        setPushStatus('unsupported');
      }
    };
    checkPushStatus();
  }, [isAuthenticated]);

  // Handle opening inbox from notification click (URL parameter)
  useEffect(() => {
    const openInbox = searchParams.get('openInbox');
    const notificationId = searchParams.get('notificationId');
    
    if (openInbox === 'true' && isAuthenticated) {
      setShowInboxPanel(true);
      
      // If a specific notification ID was provided, mark it as read
      if (notificationId) {
        handleMarkAsRead(notificationId);
      }
      
      // Clear the URL parameters
      setSearchParams({});
    }
  }, [searchParams, isAuthenticated]);

  // Handle enabling push notifications
  // Portal-specific push notification subscription that uses portal token
  const subscribeWithPortalToken = async () => {
    const portalToken = getPortalToken();
    if (!portalToken) {
      throw new Error('Not authenticated');
    }
    
    // Check platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    console.log('Push subscription attempt:', {
      platform: isIOS ? 'iOS' : 'Other',
      isStandalone: isStandalone,
      userAgent: navigator.userAgent
    });
    
    // iOS specific checks
    if (isIOS) {
      if (!isStandalone) {
        throw new Error('On iOS, you must install this app to your Home Screen first, then enable notifications from within the installed app.');
      }
      // Check iOS version - push requires 16.4+
      const match = navigator.userAgent.match(/OS (\d+)_/);
      const iosVersion = match ? parseInt(match[1]) : 0;
      console.log('iOS version detected:', iosVersion);
      if (iosVersion < 16) {
        throw new Error('Push notifications require iOS 16.4 or later. Please update your device.');
      }
    }
    
    // Request permission first
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      throw new Error('Push notifications not supported on this device/browser');
    }
    
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    if (permission !== 'granted') {
      return false;
    }
    
    // Register service worker
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      console.log('Registering service worker...');
      registration = await navigator.serviceWorker.register('/service-worker.js');
      await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Service worker timed out')), 10000))
      ]);
      registration = await navigator.serviceWorker.getRegistration();
    }
    console.log('Service worker ready:', registration?.active?.state);
    
    // Get VAPID key
    const vapidResponse = await axios.get(`${API_URL}/push/vapid-public-key`);
    const { publicKey } = vapidResponse.data;
    if (!publicKey) throw new Error('No VAPID key from server');
    console.log('VAPID public key received');
    
    // Convert base64 to Uint8Array
    const urlBase64ToUint8Array = (base64String) => {
      const padding = '='.repeat((4 - base64String.length % 4) % 4);
      const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    };
    
    // Subscribe
    console.log('Creating push subscription...');
    const subscription = await Promise.race([
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Subscription timed out')), 15000))
    ]);
    
    // Send to backend with PORTAL token
    console.log('Sending push subscription to backend...');
    const response = await axios.post(`${API_URL}/push/subscribe`, subscription.toJSON(), {
      headers: { Authorization: `Bearer ${portalToken}` }
    });
    console.log('Push subscription response:', response.data);
    
    return true;
  };

  const handleEnablePushNotifications = async () => {
    if (subscribingPush) return;
    
    setSubscribingPush(true);
    try {
      console.log('Starting push notification subscription...');
      const success = await subscribeWithPortalToken();
      console.log('Subscription result:', success);
      if (success) {
        setPushStatus('subscribed');
        toast.success('Push notifications enabled! You\'ll now receive alerts on this device.');
      } else {
        const status = await getPushNotificationStatus();
        setPushStatus(status);
        if (status === 'denied') {
          toast.error('Notification permission denied. Please enable notifications in your browser settings.');
        } else {
          toast.error('Failed to enable notifications. Please try again.');
        }
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      console.error('Error details:', error.response?.data);
      // Show specific error message
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      if (errorMsg.includes('timed out')) {
        toast.error('Connection timed out. Please check your internet connection and try again.');
      } else if (errorMsg.includes('VAPID')) {
        toast.error('Server configuration error. Please contact support.');
      } else {
        toast.error(`Failed to enable notifications: ${errorMsg}`);
      }
      setPushStatus('default');
    } finally {
      setSubscribingPush(false);
    }
  };

  useEffect(() => {
    // Clear service worker cache on mount to get fresh data
    if ('caches' in window) {
      caches.keys().then(keys => {
        keys.forEach(key => caches.delete(key));
      });
    }
    
    // CRITICAL FOR iOS PWA: Update manifest link for this customer's PWA
    // iOS Safari has a bug where it caches the manifest and doesn't re-read it
    // To force iOS to read the new manifest, we:
    // 1. Remove all existing manifest links
    // 2. Create a new link element with timestamp to bust cache
    // 3. Append it to head
    const manifestUrl = `${window.location.origin}/api/manifest/${customerId}`;
    
    // Remove ALL existing manifest links (iOS may cache old ones)
    document.querySelectorAll('link[rel="manifest"]').forEach(link => link.remove());
    
    // Create new manifest link with cache-busting
    const link = document.createElement('link');
    link.rel = 'manifest';
    // Add timestamp to bust iOS manifest cache
    link.href = `${manifestUrl}?v=${Date.now()}`;
    document.head.appendChild(link);
    console.log('PWA Manifest set to:', link.href);
    
    checkAuthRequirement();
  }, [customerId]);
  
  // Comprehensive iOS PWA meta tag updates when customer data loads
  useEffect(() => {
    if (!customer) return;
    
    // CRITICAL: iOS requires PNG icons, not SVG
    // If customer has SVG or no icon, use PNG fallback
    let iconUrl = customer.pwa_icon_url || '/logo192.png';
    if (iconUrl.endsWith('.svg') || iconUrl.startsWith('data:image/svg')) {
      iconUrl = '/logo192.png';  // iOS doesn't support SVG for home screen icons
    }
    const appName = customer.name || 'Taptile';
    const themeColor = customer.primary_color || '#2563EB';
    const bgColor = customer.background_color || '#F8FAFC';
    
    // Update theme color
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.content = themeColor;
    }
    
    // Update apple-mobile-web-app-title
    let appTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appTitleMeta) {
      appTitleMeta = document.createElement('meta');
      appTitleMeta.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appTitleMeta);
    }
    appTitleMeta.content = appName;
    
    // Update or create multiple apple-touch-icon sizes for iOS
    const iconSizes = ['', '152x152', '167x167', '180x180'];
    iconSizes.forEach(size => {
      const selector = size 
        ? `link[rel="apple-touch-icon"][sizes="${size}"]`
        : 'link[rel="apple-touch-icon"]:not([sizes])';
      let appleTouchIcon = document.querySelector(selector);
      
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        if (size) appleTouchIcon.sizes = size;
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.href = iconUrl;
    });
    
    // Update favicon
    let favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.href = iconUrl;
    }
    
    // Generate and add iOS splash screens dynamically
    // Remove existing splash screens first
    document.querySelectorAll('link[rel="apple-touch-startup-image"]').forEach(el => el.remove());
    
    // iOS splash screen configurations
    const splashScreens = [
      { width: 375, height: 667, ratio: 2 },   // iPhone SE, 8, 7, 6s, 6
      { width: 414, height: 736, ratio: 3 },   // iPhone 8 Plus, 7 Plus
      { width: 375, height: 812, ratio: 3 },   // iPhone X, XS, 11 Pro
      { width: 414, height: 896, ratio: 2 },   // iPhone XR, 11
      { width: 414, height: 896, ratio: 3 },   // iPhone XS Max, 11 Pro Max
      { width: 390, height: 844, ratio: 3 },   // iPhone 12, 13, 14
      { width: 428, height: 926, ratio: 3 },   // iPhone 12 Pro Max, 13 Pro Max
      { width: 393, height: 852, ratio: 3 },   // iPhone 14 Pro, 15, 15 Pro
      { width: 430, height: 932, ratio: 3 },   // iPhone 14 Pro Max, 15 Plus, 15 Pro Max
    ];
    
    splashScreens.forEach(screen => {
      const w = screen.width * screen.ratio;
      const h = screen.height * screen.ratio;
      
      // Create SVG splash screen with customer branding
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}">
        <rect width="100%" height="100%" fill="${bgColor}"/>
        <image href="${iconUrl}" x="50%" y="40%" width="128" height="128" style="transform:translate(-64px,-64px)"/>
      </svg>`;
      
      const dataUrl = `data:image/svg+xml,${encodeURIComponent(svg)}`;
      
      const link = document.createElement('link');
      link.rel = 'apple-touch-startup-image';
      link.href = dataUrl;
      link.media = `(device-width: ${screen.width}px) and (device-height: ${screen.height}px) and (-webkit-device-pixel-ratio: ${screen.ratio}) and (orientation: portrait)`;
      document.head.appendChild(link);
    });
    
  }, [customer]);
  
  // Update page title dynamically
  useEffect(() => {
    if (customer) {
      document.title = customer.name || 'Taptile';
    }
  }, [customer]);

  const checkAuthRequirement = async () => {
    try {
      let userIsAuthenticated = false;
      
      // Check if user is already logged in using PORTAL-SPECIFIC token only
      // This prevents cross-tenant redirect issues when a user has logged into other portals or main app
      const portalToken = getPortalToken();
      if (portalToken) {
        try {
          const userResponse = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${portalToken}` }
          });
          // Verify the user belongs to this customer's portal
          if (userResponse.data && userResponse.data.customer_id === customerId) {
            userIsAuthenticated = true;
            setIsAuthenticated(true);
            setCurrentUser(userResponse.data);
          } else {
            // Token is for a different customer, clear it
            clearPortalToken();
          }
        } catch (e) {
          // Token invalid, clear it
          clearPortalToken();
        }
      }
      
      // Check if authentication is required
      const authSettings = await axios.get(`${API_URL}/public/customers/${customerId}/auth-settings`);
      
      const authRequired = authSettings.data.require_authentication;
      const publicTiles = authSettings.data.public_tiles_enabled;
      
      setRequireAuth(authRequired);
      setPublicTilesEnabled(publicTiles);
      
      if (authRequired && !userIsAuthenticated) {
        // If public tiles are NOT enabled, show login overlay immediately
        // Otherwise, let user see public tiles and choose to sign in
        if (!publicTiles) {
          setShowLoginOverlay(true);
          setLoading(false);
          return;
        }
      }
      
      // Fetch portal data
      fetchPortalData();
    } catch (error) {
      console.error('Failed to check auth requirement:', error);
      // If endpoint fails, try to load portal anyway (fallback to old behavior)
      fetchPortalData();
    }
  };

  const handleForgotPw = async (e) => {
    e.preventDefault();
    setForgotPwLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotPwEmail.trim().toLowerCase() });
    } catch (_) { /* always show success to avoid email enumeration */ }
    setForgotPwSent(true);
    setForgotPwLoading(false);
  };

  const handlePortalLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const rememberDays = parseInt(loginForm.rememberDays) || 7;
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: loginForm.email,
        password: loginForm.password,
        remember_me: rememberDays > 1,
        remember_days: rememberDays,
        customer_id: customerId  // Scope login to this tenant for multi-tenant users
      });
      
      // Store token in portal-specific key to avoid interfering with other portals/main app
      setPortalToken(response.data.token);
      setIsAuthenticated(true);
      setCurrentUser(response.data.user);
      setShowLoginOverlay(false);
      toast.success('Welcome!');
      
      // Refresh portal data with authentication
      fetchPortalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  // Fetch notifications for inbox
  const fetchNotifications = async () => {
    const portalToken = getPortalToken();
    if (!portalToken) return;
    
    setLoadingNotifications(true);
    try {
      const response = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Fetch current user info
  const fetchCurrentUser = async () => {
    const portalToken = getPortalToken();
    if (!portalToken) return;
    
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  // Open inbox panel
  const handleOpenInbox = () => {
    if (!isAuthenticated) {
      setShowLoginOverlay(true);
      return;
    }
    setShowInboxPanel(true);
    fetchNotifications();
    fetchCurrentUser();
  };

  // Mark notification as read (with auto-mark when viewing)
  const handleMarkAsRead = async (notificationId) => {
    // Check if already marked as read by this user
    const notification = notifications.find(n => n.id === notificationId);
    if (notification?.read_by?.includes(currentUser?.id)) {
      return; // Already read
    }
    
    try {
      await axios.patch(`${API_URL}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${getPortalToken()}` }
      });
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.id === notificationId 
          ? { ...n, read_by: [...(n.read_by || []), currentUser?.id] }
          : n
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
      // Don't show toast for auto-mark failures
    }
  };

  // Mark all unread notifications as read when inbox is opened
  const handleMarkAllVisibleAsRead = async () => {
    if (!currentUser?.id) return;
    
    const unreadNotifications = notifications.filter(
      n => !n.read_by?.includes(currentUser.id)
    );
    
    // Mark each unread notification as read (silently)
    for (const notif of unreadNotifications) {
      handleMarkAsRead(notif.id);
    }
  };

  // Delete a single notification
  const handleDeleteNotification = async (notificationId) => {
    setDeletingNotification(notificationId);
    try {
      await axios.delete(`${API_URL}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${getPortalToken()}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    } finally {
      setDeletingNotification(null);
    }
  };

  // Delete all notifications
  const handleDeleteAllNotifications = async () => {
    if (!confirm('Are you sure you want to delete all notifications?')) return;
    
    try {
      await axios.delete(`${API_URL}/notifications/all`, {
        headers: { Authorization: `Bearer ${getPortalToken()}` }
      });
      setNotifications([]);
      toast.success('All notifications deleted');
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
      toast.error('Failed to delete notifications');
    }
  };

  // Check if notification is read
  const isNotificationRead = (notification) => {
    return notification.read_by?.includes(currentUser?.id);
  };

  // Filter notifications based on unread toggle
  const filteredNotifications = showUnreadOnly 
    ? notifications.filter(n => !isNotificationRead(n))
    : notifications;

  // Handle logout from PWA
  const handleLogout = () => {
    clearPortalToken();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setNotifications([]);
    setShowInboxPanel(false);
    toast.success('Logged out');
    
    // Refresh to show appropriate tiles
    fetchPortalData();
  };

  // Handle pull-to-refresh
  const handleRefresh = useCallback(async () => {
    try {
      // Add cache-busting timestamp to prevent stale data
      const timestamp = new Date().getTime();
      const config = {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        params: { _t: timestamp }
      };
      
      const [tilesData, articleData, customerData] = await Promise.all([
        axios.get(`${API_URL}/public/customers/${customerId}/tiles`, config).then(r => r.data),
        axios.get(`${API_URL}/public/customers/${customerId}/top-articles`, config).then(r => r.data).catch(() => []),
        axios.get(`${API_URL}/public/customers/${customerId}`, config).then(r => r.data)
      ]);
      
      setTiles(tilesData);
      setTopArticles(Array.isArray(articleData) ? articleData : articleData ? [articleData] : []);
      setCustomer(customerData);
      if (customerData?.grid_cols) {
        setGridCols(customerData.grid_cols);
      }

      // Refresh Nexus articles if enabled
      const articleLimit = customerData?.news_article_count || 3;
      if (customerData?.nexus_articles_enabled) {
        const nexusData = await axios.get(
          `${API_URL}/public/customers/${customerId}/nexus-articles`,
          { params: { limit: articleLimit } }
        ).then(r => r.data).catch(() => null);
        setNexusArticles(nexusData?.articles || []);
      }

      // Refresh Nexus events if enabled
      if (customerData?.nexus_events_enabled) {
        const now = new Date().toISOString().slice(0, 10);
        const eventsData = await axios.get(
          `${API_URL}/public/customers/${customerId}/nexus-events`,
          { params: { from: now, limit: 3 } }
        ).then(r => r.data).catch(() => null);
        setNexusEvents(eventsData?.events || []);
      }
      
      // Also refresh notifications if authenticated
      if (isAuthenticated) {
        const portalToken = getPortalToken();
        if (portalToken) {
          try {
            const notifResponse = await axios.get(`${API_URL}/notifications`, {
              headers: { Authorization: `Bearer ${portalToken}` }
            });
            setNotifications(notifResponse.data);
          } catch (e) {
            console.error('Failed to refresh notifications:', e);
          }
        }
      }
      
      toast.success('Refreshed!');
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Failed to refresh');
    }
  }, [customerId, isAuthenticated]);

  const fetchPortalData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add cache-busting timestamp to prevent stale data
      const timestamp = new Date().getTime();
      const config = {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        params: { _t: timestamp }
      };
      
      const [tilesData, articleData, customerData] = await Promise.all([
        axios.get(`${API_URL}/public/customers/${customerId}/tiles`, config).then(r => r.data),
        axios.get(`${API_URL}/public/customers/${customerId}/top-articles`, config).then(r => r.data).catch(() => []),
        axios.get(`${API_URL}/public/customers/${customerId}`, config).then(r => r.data)
      ]);
      
      setTiles(tilesData);
      setTopArticles(Array.isArray(articleData) ? articleData : articleData ? [articleData] : []);
      setCustomer(customerData);
      if (customerData?.grid_cols) {
        setGridCols(customerData.grid_cols);
      }

      // Fetch Nexus articles if enabled
      const articleLimit = customerData?.news_article_count || 3;
      if (customerData?.nexus_articles_enabled) {
        const nexusData = await axios.get(
          `${API_URL}/public/customers/${customerId}/nexus-articles`,
          { params: { limit: articleLimit } }
        ).then(r => r.data).catch(() => null);
        setNexusArticles(nexusData?.articles || []);
      }

      // Fetch Nexus events if enabled
      if (customerData?.nexus_events_enabled) {
        const now = new Date().toISOString().slice(0, 10);
        const eventsData = await axios.get(
          `${API_URL}/public/customers/${customerId}/nexus-events`,
          { params: { from: now, limit: 3 } }
        ).then(r => r.data).catch(() => null);
        setNexusEvents(eventsData?.events || []);
      }
    } catch (error) {
      console.error('Failed to load portal:', error);
      setError('Portal not found or unavailable');
    } finally {
      setLoading(false);
    }
  };

  const socialMediaIcons = {
    facebook: { icon: Facebook, color: '#1877F2' },
    twitter: { icon: X, color: '#000000' },
    linkedin: { icon: Linkedin, color: '#0A66C2' },
    instagram: { icon: Instagram, color: '#E4405F' },
    youtube: { icon: Youtube, color: '#FF0000' },
    tiktok: { icon: TikTokIcon, color: '#000000' },
    bluesky: { icon: BlueSkyIcon, color: '#0085FF' },
    custom: { icon: Link2, color: '#334155' }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: customer?.background_color || '#F8FAFC' }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <h1 className="text-2xl font-bold mb-4">Portal Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate(`/login?returnTo=/portal/${customerId}`)}>Go to Login</Button>
        </div>
      </div>
    );
  }

  const socialLinksSection = customer?.social_links_enabled && customer?.social_links && (() => {
    const enabledCount = countEnabledSocialLinks(customer.social_links);
    if (enabledCount === 0) return null;
    const sz = socialLinkSizeClasses(enabledCount);
    return (
      <Card className="border-none shadow-sm" style={{ backgroundColor: customer.social_background_color || '#FFFFFF' }}>
        <CardContent className="p-4 sm:p-6">
          {customer?.show_social_section_title !== false && customer?.social_section_title && (
            <h3 className="text-sm font-medium mb-4 text-center" style={{ color: customer.social_section_title_color || customer.font_color || '#1E293B' }}>
              {customer.social_section_title}
            </h3>
          )}
          <div className={`flex ${sz.gap} justify-center overflow-x-auto pb-2`} data-testid="social-links-row">
            {SOCIAL_URL_KEYS.map((platform) => {
              const url = customer.social_links[platform];
              const enabled = customer.social_links[`${platform}_enabled`];
              if (!url || !enabled || !socialMediaIcons[platform]) return null;
              const { icon: Icon, color } = socialMediaIcons[platform];
              const label = platform === 'custom'
                ? (customer.social_links.custom_label || 'Link')
                : platform;
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  aria-label={label}
                  data-testid={`social-link-${platform}`}
                  className={`flex items-center justify-center ${sz.button} rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:-translate-y-1 active:scale-95 flex-shrink-0`}
                  style={{ color }}
                >
                  <Icon className={sz.icon} />
                </a>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  })();

  const headerImageSection = customer?.header_image_url && (
    <div className="w-full mb-6" style={{ backgroundColor: customer?.header_background_color || '#FFFFFF' }}>
      <img 
        src={customer.header_image_url} 
        alt="Header" 
        className="w-full h-auto object-contain"
      />
    </div>
  );

  const tilesSection = (
    <>
      {/* Show tiles section title only if toggle is enabled */}
      {customer?.show_tiles_section_title === true && customer?.tiles_section_title && (
        <div className="mb-6">
          <h3 className="text-base sm:text-lg font-semibold" style={{ color: customer?.tiles_section_title_color || customer?.font_color || '#1E293B' }}>
            {customer.tiles_section_title}
          </h3>
        </div>
      )}

      {tiles.length === 0 ? (
        <Card style={{ backgroundColor: customer?.tile_color || '#FFFFFF' }}>
          <CardContent className="py-12 text-center">
            <p style={{ color: customer?.font_color || '#1E293B' }}>No tiles available yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className={`grid ${
          gridCols === 2 ? 'grid-cols-2' : 
          gridCols === 3 ? 'grid-cols-3' : 
          'grid-cols-4'
        } gap-3 sm:gap-4`}>
          {tiles.map((tile) => (
            <div key={tile.id} className={`tile-size-${customer?.tile_size || 'default'}`}>
              <TileCard 
                tile={tile} 
                tileColor={customer?.tile_color}
                tileFontColor={customer?.tile_font_color}
                tileFontSize={customer?.tile_font_size}
                tileStyle={customer?.tile_style}
                tileShape={customer?.tile_shape}
                showTitle={customer?.show_tile_title !== false}
                showIcon={customer?.show_tile_icon !== false}
                primaryColor={customer?.primary_color}
                onChatOpen={(tile) => setActiveChatTile(tile)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );

  const newsSection = customer?.news_enabled && (nexusArticles.length > 0 || topArticles.length > 0) && (
    <Card 
      className="border-l-4" 
      style={{ 
        borderLeftColor: customer?.primary_color || '#2563EB',
        backgroundColor: customer?.tile_color || '#FFFFFF'
      }}
      data-testid="news-section-card"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ color: customer?.primary_color || '#2563EB' }}>
          <Newspaper className="w-5 h-5" />
          Latest News
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {(nexusArticles.length > 0 ? nexusArticles : topArticles).filter(Boolean).map((article) => (
            <div key={article.id} className="space-y-3 pb-6 border-b last:border-b-0 last:pb-0">
              <div className="flex gap-4">
                {article.image_url && (
                  <img
                    src={article.image_url.startsWith('http') ? article.image_url : `${process.env.REACT_APP_BACKEND_URL}${article.image_url}`}
                    alt={article.title}
                    className="w-24 h-24 object-cover rounded flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold mb-2 line-clamp-2" style={{ color: customer?.font_color || '#1E293B' }}>
                    {article.title}
                  </h4>
                  {article.excerpt && (
                    <p className="text-sm line-clamp-2 mb-2" style={{ color: customer?.font_color || '#64748B' }}>
                      {article.excerpt}
                    </p>
                  )}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-sm font-medium"
                    style={{ color: customer?.primary_color || '#2563EB' }}
                    onClick={async () => {
                      // Open dialog immediately, then fetch full Nexus detail
                      setSelectedArticle(article);
                      if (customer?.nexus_articles_enabled) {
                        setArticleLoading(true);
                        try {
                          const res = await axios.get(
                            `${API_URL}/public/customers/${customerId}/nexus-articles/${article.id}`
                          );
                          setSelectedArticle(res.data);
                        } catch (e) {
                          console.error('Article detail fetch failed:', e);
                          // keep the list article shown
                        } finally {
                          setArticleLoading(false);
                        }
                      }
                    }}
                  >
                    Read more →
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const nexusEventsSection = customer?.nexus_events_enabled ? (
    <Card
      className="border-l-4"
      style={{ borderLeftColor: customer?.primary_color || '#2563EB', backgroundColor: customer?.tile_color || '#FFFFFF' }}
      data-testid="events-section-card"
    >
      <CardHeader className="pb-2">
        <CardTitle
          className="flex items-center text-base"
          style={{ color: customer?.primary_color || '#2563EB' }}
        >
          <Calendar className="w-5 h-5 mr-2" />
          Upcoming Events
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {nexusEvents.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">No upcoming events</p>
        ) : (
          <div className="space-y-2">
            {nexusEvents.map(ev => {
              const isFull = ev.rsvp_full && !ev.waitlist_enabled;
              return (
                <button
                  key={ev.id}
                  data-testid={`event-preview-${ev.id}`}
                  className="w-full flex items-start gap-3 p-2.5 rounded-lg border border-gray-100 hover:shadow-sm transition-shadow text-left cursor-pointer"
                  style={{ borderLeftColor: ev.color || customer?.primary_color || '#2563EB', borderLeftWidth: 3 }}
                  onClick={() => setSelectedEvent(ev)}
                >
                  <div
                    className="flex-shrink-0 w-10 text-center rounded-md py-1"
                    style={{ backgroundColor: `${customer?.primary_color || '#2563EB'}15` }}
                  >
                    <p className="text-xs font-bold leading-none" style={{ color: customer?.primary_color || '#2563EB' }}>
                      {new Date(ev.start).toLocaleString('en-GB', { day: 'numeric' })}
                    </p>
                    <p className="text-xs leading-none mt-0.5" style={{ color: customer?.primary_color || '#2563EB' }}>
                      {new Date(ev.start).toLocaleString('en-GB', { month: 'short' })}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight truncate" style={{ color: customer?.font_color || '#1E293B' }}>
                      {ev.title}
                    </p>
                    {ev.location && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate flex items-center gap-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />{ev.location}
                      </p>
                    )}
                    {!ev.all_day && (
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        {new Date(ev.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  {ev.rsvp_enabled && (
                    <div className="flex-shrink-0 flex items-center">
                      <Badge variant={isFull ? 'destructive' : 'outline'} className="text-xs">
                        {isFull ? 'Full' : ev.waitlist_enabled && ev.rsvp_full ? 'Waitlist' : 'RSVP'}
                      </Badge>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  ) : null;

  const renderContent = () => {
    // If auth required but not authenticated and no public tiles, show placeholder
    if (requireAuth && !isAuthenticated && !publicTilesEnabled) {
      return (
        <div className="text-center py-12">
          <LogIn className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2" style={{ color: customer?.font_color || '#1E293B' }}>
            Sign in to view content
          </h2>
          <p className="text-muted-foreground mb-6">
            This portal requires authentication to access
          </p>
          <Button 
            onClick={() => setShowLoginOverlay(true)}
            style={{ backgroundColor: customer?.primary_color || '#2563EB' }}
          >
            <LogIn className="h-4 w-4 mr-2" />
            Sign In
          </Button>
        </div>
      );
    }
    
    const position = customer?.social_links_position || 'top';
    
    if (position === 'top') {
      return (
        <div className="space-y-8">
          {socialLinksSection}
          {tilesSection}
          {nexusEventsSection}
          {newsSection}
        </div>
      );
    } else if (position === 'between') {
      return (
        <div className="space-y-8">
          {tilesSection}
          {socialLinksSection}
          {nexusEventsSection}
          {newsSection}
        </div>
      );
    } else {
      return (
        <div className="space-y-8">
          {tilesSection}
          {nexusEventsSection}
          {newsSection}
          {socialLinksSection}
        </div>
      );
    }
  };

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent={
        <div className="flex justify-center items-center py-4">
          <RefreshCw className="w-6 h-6 text-muted-foreground animate-pulse" />
          <span className="ml-2 text-sm text-muted-foreground">Pull to refresh...</span>
        </div>
      }
      refreshingContent={
        <div className="flex justify-center items-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-sm text-muted-foreground">Refreshing...</span>
        </div>
      }
      resistance={2.5}
      maxPullDownDistance={100}
      pullDownThreshold={67}
    >
    <div 
      className="min-h-screen relative"
      style={{ 
        backgroundColor: customer?.background_color || '#F8FAFC',
        fontFamily: customer?.font_family || 'Inter'
      }}
    >
      {/* Login Overlay - Shows when authentication is required and user not logged in */}
      {showLoginOverlay && !isAuthenticated && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLoginOverlay(false); }}
        >
          <Card className="w-full max-w-md relative">
            <button
              type="button"
              onClick={() => setShowLoginOverlay(false)}
              className="absolute top-3 right-3 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Close login"
              data-testid="close-login-overlay-btn"
            >
              <X className="h-5 w-5" />
            </button>
            <CardHeader className="text-center">
              {customer?.pwa_icon_url && (
                <img 
                  src={customer.pwa_icon_url} 
                  alt={customer?.name} 
                  className="w-16 h-16 mx-auto mb-4 rounded-lg"
                />
              )}
              <CardTitle>{showForgotPw ? 'Reset Password' : (customer?.name || 'Sign In')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {showForgotPw ? "Enter your email and we'll send a reset link" : 'Sign in to access this portal'}
              </p>
            </CardHeader>
            <CardContent>
              {/* ── Forgot password form ── */}
              {showForgotPw ? (
                forgotPwSent ? (
                  <div className="space-y-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      If an account exists for that email, a reset link has been sent. Please check your inbox.
                    </p>
                    <Button className="w-full" onClick={() => { setShowForgotPw(false); setForgotPwSent(false); }}>
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPw} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Email</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="your@email.com"
                        value={forgotPwEmail}
                        onChange={(e) => setForgotPwEmail(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={forgotPwLoading}>
                      {forgotPwLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Send Reset Link
                    </Button>
                    <button type="button" onClick={() => setShowForgotPw(false)} className="w-full text-sm text-muted-foreground hover:text-foreground text-center mt-1">
                      Back to Sign In
                    </button>
                  </form>
                )
              ) : (
              /* ── Sign in form ── */
              <form onSubmit={handlePortalLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="portal-email">Email</Label>
                  <Input
                    id="portal-email"
                    type="email"
                    placeholder="your@email.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="portal-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="portal-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="remember-duration">Stay logged in</Label>
                  <Select 
                    value={loginForm.rememberDays} 
                    onValueChange={(value) => setLoginForm({ ...loginForm, rememberDays: value })}
                  >
                    <SelectTrigger id="remember-duration">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">This session only</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading}>
                  {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
                  Sign In
                </Button>
                <button
                  type="button"
                  onClick={() => { setShowForgotPw(true); setForgotPwEmail(loginForm.email); setForgotPwSent(false); }}
                  className="w-full text-sm text-primary hover:underline text-center"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Header Image - Full Width, Outside Container */}
      {headerImageSection}
      
      <div className="py-4 sm:py-6 px-3 sm:px-4">
        <div className="max-w-6xl mx-auto">
          {/* Bottom Install Banner - Android only (iOS users download from App Store) */}
          {!isIOS && (
            <InstallPrompt 
              variant="banner"
              primaryColor={customer?.primary_color}
              customerName={customer?.name}
            />
          )}

          {/* iOS App Store Banner — shown in Safari (not standalone) so iOS users download the native app */}
          {isIOS && !isStandalone && (
            <a
              href={process.env.REACT_APP_IOS_APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="app-store-banner"
              className="flex items-center gap-3 mb-4 px-4 py-3 rounded-xl border bg-black text-white hover:bg-gray-900 transition-colors"
              style={{ borderColor: '#333' }}
            >
              <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0" fill="white">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 leading-none mb-0.5">Download on the</p>
                <p className="text-sm font-semibold leading-none">App Store</p>
              </div>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          )}
          
          {/* Sign In/Out Button - Top right */}
          <div className="mb-4 flex items-center justify-end gap-2">
            <div>
            {!isAuthenticated ? (
              <Button
                onClick={() => setShowLoginOverlay(true)}
                size="sm"
                style={{
                  backgroundColor: customer?.button_color || customer?.primary_color || '#2563EB',
                  color: getButtonTextColor(customer?.button_color || customer?.primary_color || '#2563EB'),
                  border: 'none'
                }}
              >
                Sign In
              </Button>
            ) : (
              <Button 
                onClick={() => {
                  clearPortalToken();
                  setIsAuthenticated(false);
                  setCurrentUser(null);
                  if (requireAuth) setShowLoginOverlay(true);
                }} 
                size="sm"
                variant="outline"
                style={{
                  borderColor: customer?.button_color || customer?.primary_color || '#2563EB',
                  color: customer?.button_color || customer?.primary_color || '#2563EB'
                }}
              >
                Sign Out
              </Button>
            )}
            </div>
          </div>
          
          {/* Header Text */}
          {customer?.show_header_text !== false && customer?.header_text && (
            <div className={`mb-6 sm:mb-8 ${
              customer?.header_text_align === 'center' ? 'text-center' : 
              customer?.header_text_align === 'right' ? 'text-right' : ''
            }`}>
              <h1 
                className={`font-bold ${
                  customer?.header_text_size === 'sm' ? 'text-lg sm:text-xl' :
                  customer?.header_text_size === 'base' ? 'text-xl sm:text-2xl' :
                  customer?.header_text_size === 'lg' ? 'text-2xl sm:text-3xl' :
                  customer?.header_text_size === '2xl' ? 'text-3xl sm:text-4xl' :
                  customer?.header_text_size === '3xl' ? 'text-4xl sm:text-5xl' :
                  'text-2xl sm:text-3xl md:text-4xl'
                }`}
                style={{ 
                  color: customer?.header_text_color || customer?.primary_color || '#2563EB',
                  fontFamily: customer?.header_text_font || customer?.font_family || 'Inter'
                }}
              >
                {customer.header_text}
              </h1>
            </div>
          )}

          {/* Welcome Message */}
          {customer?.show_welcome_message !== false && currentUser && (
            <p
              className="text-sm mb-4 opacity-90"
              style={{ color: customer?.header_text_color || customer?.primary_color || '#2563EB' }}
            >
              Welcome, {currentUser.name || currentUser.email}
            </p>
          )}
          
          {/* View Inbox Button - visible to all users; unauthenticated users are redirected to login */}
          {customer?.show_inbox_button !== false && (
            <div className="mb-4 space-y-2">
              <Button 
                onClick={handleOpenInbox}
                variant="outline"
                className="w-full gap-2"
                style={{ 
                  borderColor: customer?.primary_color || '#2563EB',
                  color: customer?.primary_color || '#2563EB'
                }}
              >
                <Inbox className="h-4 w-4" />
                View Inbox
                {notifications.filter(n => !n.read_by?.includes(currentUser?.id)).length > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {notifications.filter(n => !n.read_by?.includes(currentUser?.id)).length}
                  </span>
                )}
              </Button>
              
              {/* Push Notification Enable Button - Only for authenticated users */}
              {isAuthenticated && pushStatus !== 'subscribed' && pushStatus !== 'unsupported' && (
                <Button 
                  onClick={handleEnablePushNotifications}
                  variant="default"
                  className="w-full gap-2 animate-pulse"
                  disabled={subscribingPush || pushStatus === 'denied'}
                  style={{ 
                    backgroundColor: pushStatus === 'denied' ? '#9CA3AF' : (customer?.primary_color || '#2563EB')
                  }}
                >
                  {subscribingPush ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Enabling...
                    </>
                  ) : pushStatus === 'denied' ? (
                    <>
                      <Bell className="h-4 w-4" />
                      Notifications Blocked
                    </>
                  ) : (
                    <>
                      <BellRing className="h-4 w-4" />
                      🔔 Enable Push Notifications
                    </>
                  )}
                </Button>
              )}
              {isAuthenticated && pushStatus === 'subscribed' && (
                <p className="text-xs text-center text-green-600 flex items-center justify-center gap-1">
                  <Bell className="h-3 w-3" />
                  Push notifications enabled
                </p>
              )}
              {isAuthenticated && pushStatus === 'denied' && (
                <p className="text-xs text-center text-muted-foreground">
                  Enable notifications in your browser settings to receive alerts
                </p>
              )}
              
              {/* Legacy unsupported message - for non-iOS devices */}
              {isAuthenticated && pushStatus === 'unsupported' && !isIOS && (
                <p className="text-xs text-center text-muted-foreground">
                  Push notifications are not supported on this device/browser
                </p>
              )}
            </div>
          )}
          
          {renderContent()}
          
          {/* Portal Access Button - Below News when auth required but showing public tiles */}
          {requireAuth && publicTilesEnabled && !isAuthenticated && (
            <div className="mt-8 text-center">
              <Button 
                onClick={() => setShowLoginOverlay(true)}
                variant="default"
                size="lg"
                className="gap-2"
                style={{ backgroundColor: customer?.primary_color || '#2563EB' }}
              >
                <LogIn className="h-5 w-5" />
                Sign In for Full Access
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                Sign in to view all content and features
              </p>
            </div>
          )}
        
          <footer className="mt-12 pt-6 border-t text-center">
            <a
              href="/"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors block mb-2"
              data-testid="switch-organisation-link"
              title="Switch Organisation"
            >
              Switch Organisation
            </a>
            <p className="text-xs text-muted-foreground">
              Made by <span className="font-medium">Dynatex SaaS</span>
            </p>
          </footer>
        </div>
      </div>

      {/* Article Detail Dialog */}
      <Dialog open={!!selectedArticle} onOpenChange={(open) => { if (!open) { setSelectedArticle(null); setArticleLoading(false); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedArticle?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedArticle?.image_url && (
              <img 
                src={selectedArticle.image_url.startsWith('http') ? selectedArticle.image_url : `${process.env.REACT_APP_BACKEND_URL}${selectedArticle.image_url}`}
                alt={selectedArticle.title}
                className="w-full h-48 object-cover rounded-lg"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}

            {/* Loading spinner while fetching full content */}
            {articleLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading full article...</span>
              </div>
            ) : (
              <>
                {/* Only show excerpt if there is no richer content — prevents showing "summary" twice */}
                {selectedArticle?.excerpt &&
                  !selectedArticle?.content_html && !selectedArticle?.body_html &&
                  !selectedArticle?.html && !selectedArticle?.content && !selectedArticle?.body && (
                  <p className="text-muted-foreground italic border-l-4 border-primary pl-4">
                    {selectedArticle.excerpt}
                  </p>
                )}
                <div>
                  {(selectedArticle?.content_html || selectedArticle?.body_html || selectedArticle?.html) ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: selectedArticle.content_html || selectedArticle.body_html || selectedArticle.html }}
                      style={{ lineHeight: '1.75', fontSize: '0.95rem' }}
                      className="[&_p]:mb-4 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:underline [&_strong]:font-semibold [&_img]:max-w-full [&_img]:rounded [&_img]:my-3 [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-3"
                    />
                  ) : (selectedArticle?.content || selectedArticle?.body) ? (
                    (selectedArticle.content || selectedArticle.body).split('\n').map((paragraph, index) => (
                      paragraph.trim() && <p key={index} className="mb-3 leading-relaxed">{paragraph}</p>
                    ))
                  ) : selectedArticle?.url ? null : (
                    <p className="text-muted-foreground text-sm">No full content available for this article.</p>
                  )}
                </div>
                {selectedArticle?.url && (
                  <div className="pt-2">
                    <a
                      href={selectedArticle.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium underline"
                      style={{ color: customer?.primary_color || '#2563EB' }}
                    >
                      Read full article →
                    </a>
                  </div>
                )}
                {(selectedArticle?.author || selectedArticle?.created_at) && (
                  <div className="text-sm text-muted-foreground pt-4 border-t">
                    {selectedArticle?.author && <>By {selectedArticle.author}</>}
                    {selectedArticle?.author && selectedArticle?.created_at && <> • </>}
                    {selectedArticle?.created_at && new Date(selectedArticle.created_at).toLocaleDateString()}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Event RSVP Modal */}
      {selectedEvent && (
        <PortalRsvpModal
          event={selectedEvent}
          customerId={customerId}
          currentUser={currentUser}
          primaryColor={customer?.primary_color}
          onClose={() => setSelectedEvent(null)}
        />
      )}

      {/* Ubots Chat Overlay — Portal renders above all stacking contexts */}
      {activeChatTile && ReactDOM.createPortal((() => {
        const raw = activeChatTile.embed_code || activeChatTile.url || '';
        // Extract UUID whether a full script or bare ID was stored
        const match = raw.match(/ubots\.chat\/chat\/([a-f0-9-]{36})/i);
        const embedId = match ? match[1] : raw.replace('https://ubots.chat/chat/', '').trim();
        const chatUrl = `https://ubots.chat/chat/${embedId}`;
        return (
          <div
            className="fixed inset-0 flex flex-col"
            style={{ zIndex: 9999, backgroundColor: customer?.primary_color || '#2563EB' }}
            data-testid="chat-overlay"
          >
            {/* Header bar */}
            <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
              <span className="text-white font-semibold text-base truncate">
                {activeChatTile.title || 'Chat'}
              </span>
              <button
                onClick={() => setActiveChatTile(null)}
                className="text-white opacity-80 hover:opacity-100 p-1 rounded-full"
                data-testid="chat-overlay-close"
                aria-label="Close chat"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            {/* Full-height iframe */}
            <iframe
              src={chatUrl}
              title={activeChatTile.title || 'Chat'}
              className="flex-1 w-full border-0 bg-white"
              allow="microphone; camera"
              data-testid="chat-iframe"
            />
          </div>
        );
      })(), document.body)}

      {/* iOS Install Guide Dialog */}
      <Dialog open={showIOSInstallGuide} onOpenChange={setShowIOSInstallGuide}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="ios-install-guide-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              📲 Install {customer?.name || 'App'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              To receive push notifications on your iPhone/iPad, you need to install this app to your Home Screen.
            </p>
            
            {/* Copy URL Button */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="font-semibold text-green-800 text-sm mb-2">Step 1: Copy this URL</p>
              <div className="flex gap-2">
                <code className="flex-1 text-xs bg-white p-2 rounded border break-all">
                  {window.location.href}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('URL copied!');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
            
            {/* Airplane Mode Workaround - THE KEY FIX */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="font-semibold text-amber-800 text-sm flex items-center gap-1">
                ✈️ iOS Workaround (Recommended)
              </p>
              <p className="text-amber-700 text-xs mt-1">
                iOS Safari has a bug that loses the URL path. Use this workaround:
              </p>
              <ol className="text-xs text-amber-800 mt-2 space-y-1 list-decimal list-inside">
                <li><strong>Copy the URL above</strong></li>
                <li><strong>Turn ON Airplane Mode</strong></li>
                <li><strong>Paste URL in Safari</strong> and tap Go (will fail to load)</li>
                <li><strong>Tap Share → Add to Home Screen</strong></li>
                <li><strong>Tap Add</strong>, then turn OFF Airplane Mode</li>
              </ol>
              <p className="text-amber-600 text-xs mt-2 italic">
                Airplane mode prevents Safari from resetting the URL to the root domain.
              </p>
            </div>
            
            {/* Standard Method (less reliable) */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <h4 className="font-semibold text-gray-700 text-sm">Standard Method (may not work):</h4>
              <ol className="space-y-1 text-xs text-gray-600 list-decimal list-inside">
                <li>Tap the Share button (square with arrow)</li>
                <li>Tap &quot;Add to Home Screen&quot;</li>
                <li>Verify URL shows /portal/... (not just taptile.app)</li>
                <li>Tap Add</li>
              </ol>
            </div>
            
            {/* After Installation */}
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-semibold text-blue-800 text-sm">After Installation:</h4>
              <p className="text-blue-700 text-xs mt-1">
                Open the app from your Home Screen and tap &quot;Enable Push Notifications&quot; to receive alerts.
              </p>
            </div>
            
            <div className="text-xs text-gray-600 bg-gray-100 p-3 rounded-lg">
              <p className="font-medium">Requirements:</p>
              <ul className="mt-1 space-y-1 list-disc list-inside">
                <li>iOS 16.4 or later for push notifications</li>
                <li>Must open from Home Screen icon (not Safari)</li>
              </ul>
            </div>
            
            <Button 
              onClick={() => setShowIOSInstallGuide(false)}
              className="w-full"
              style={{ backgroundColor: customer?.primary_color || '#2563EB' }}
            >
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inbox Slide-out Panel */}
      <Sheet open={showInboxPanel} onOpenChange={setShowInboxPanel}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5" />
              My Inbox
            </SheetTitle>
            <SheetDescription>
              View your notifications and profile
            </SheetDescription>
          </SheetHeader>
          
          {/* Tab Buttons */}
          <div className="flex gap-2 mt-4 mb-4">
            <Button
              variant={inboxTab === 'notifications' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInboxTab('notifications')}
              className="flex-1 gap-2"
            >
              <Bell className="h-4 w-4" />
              Notifications
            </Button>
            <Button
              variant={inboxTab === 'profile' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInboxTab('profile')}
              className="flex-1 gap-2"
            >
              <User className="h-4 w-4" />
              Profile
            </Button>
          </div>

          {inboxTab === 'notifications' && (
            <div className="space-y-3">
              {/* Notification Controls */}
              {notifications.length > 0 && (
                <div className="flex items-center justify-between gap-2 pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <Button
                      variant={showUnreadOnly ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                      className="text-xs h-7"
                    >
                      {showUnreadOnly ? 'Show All' : 'Unread Only'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkAllVisibleAsRead}
                      className="text-xs h-7"
                    >
                      <CheckCheck className="h-3 w-3 mr-1" />
                      Mark All Read
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteAllNotifications}
                    className="text-xs h-7 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete All
                  </Button>
                </div>
              )}
              
              {loadingNotifications ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}</p>
                </div>
              ) : (
                filteredNotifications.map((notification) => {
                  const isRead = isNotificationRead(notification);
                  return (
                    <Card 
                      key={notification.id} 
                      className={`relative transition-all ${!isRead ? 'border-l-4 border-l-primary bg-primary/5' : ''}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-1">
                            <Bell className={`h-4 w-4 ${!isRead ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-sm ${!isRead ? 'font-semibold' : 'font-medium'}`}>
                                {notification.title}
                              </p>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {!isRead && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleMarkAsRead(notification.id)}
                                    className="h-6 w-6 p-0"
                                    title="Mark as read"
                                  >
                                    <Check className="h-3 w-3" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteNotification(notification.id)}
                                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                  disabled={deletingNotification === notification.id}
                                  title="Delete"
                                >
                                  {deletingNotification === notification.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <p className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                              </p>
                              {isRead && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <CheckCheck className="h-3 w-3" />
                                  Read
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {inboxTab === 'profile' && currentUser && (
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="font-medium">{currentUser.name || 'Not set'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="font-medium">{currentUser.email}</p>
                    {currentUser.wonde_id && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Synced from school system
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Push Notification Settings */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">Push Notifications</Label>
                  </div>
                  
                  {pushStatus === 'subscribed' ? (
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                      <Check className="h-4 w-4" />
                      <span>Push notifications are enabled on this device</span>
                    </div>
                  ) : pushStatus === 'denied' ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                        <Bell className="h-4 w-4" />
                        <span>Notifications are blocked</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        To enable notifications, go to your browser/device settings and allow notifications for this app.
                      </p>
                    </div>
                  ) : pushStatus === 'unsupported' ? (
                    <div className="space-y-2">
                      <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                        {/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.matchMedia('(display-mode: standalone)').matches ? (
                          <>
                            <p className="font-medium">📱 Install App First</p>
                            <p className="mt-1">To receive push notifications on iOS:</p>
                            <ol className="list-decimal ml-4 mt-2 space-y-1 text-xs">
                              <li>Tap the <strong>Share</strong> button in Safari</li>
                              <li>Select <strong>&quot;Add to Home Screen&quot;</strong></li>
                              <li>Open the app from your Home Screen</li>
                              <li>Then enable notifications here</li>
                            </ol>
                          </>
                        ) : (
                          <p>Push notifications are not supported on this device/browser.</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Enable push notifications to receive alerts when new messages arrive.
                      </p>
                      <Button 
                        onClick={handleEnablePushNotifications}
                        variant="default"
                        className="w-full gap-2"
                        disabled={subscribingPush}
                        style={{ backgroundColor: customer?.primary_color || '#2563EB' }}
                      >
                        {subscribingPush ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Enabling...
                          </>
                        ) : (
                          <>
                            <BellRing className="h-4 w-4" />
                            Enable Push Notifications
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
    </PullToRefresh>
  );
}
